import { useCallback, useEffect } from 'react';
import { Flex, RemoteImage } from '@banhala/ui-kit';
import { AD_RESPONSE_TYPE, RETAIL_AD_SLOT_ID } from 'shared/src/api';
import { useOpenDeeplink } from 'shared/src/hooks';
import { useRetailAd } from '../queries/useRetailAd';
import { usePostRetailAdDisplayEvent } from '../queries/usePostRetailAdDisplayEvent';
import { useLogRetailAd } from '../hooks/useLogRetailAd';
import { isNil } from 'shared/src/utils';

const HORIZONTAL_PADDING = 16;

interface RetailAdBannerProps {
  contentWidth?: number | null;
}

export const RetailAdBanner = ({ contentWidth }: RetailAdBannerProps) => {
  const { data: retailAd } = useRetailAd(RETAIL_AD_SLOT_ID.OVERVIEW_TOP_BANNER);
  const { openDeeplink } = useOpenDeeplink();
  const { logDisplayAds, logClickAds } = useLogRetailAd(
    RETAIL_AD_SLOT_ID.OVERVIEW_TOP_BANNER,
  );
  const { mutate: sendDisplayEvent } = usePostRetailAdDisplayEvent();

  const handlePress = useCallback(() => {
    if (retailAd?.ad_info?.creative_link == null) return;
    logClickAds();
    openDeeplink(retailAd.ad_info.creative_link);
  }, [retailAd, openDeeplink, logClickAds]);

  const isVisible =
    retailAd != null &&
    retailAd.ad_response_type === AD_RESPONSE_TYPE.OK &&
    retailAd.ad_info != null &&
    contentWidth != null;

  const executableId = retailAd?.ad_info?.ad_executable_id;

  useEffect(() => {
    if (!isVisible) return;
    logDisplayAds();

    // iOS: OverviewRetailAdCell → retailAdClient.sendDisplayEvent
    // Android: RetailAdEventStateImpl.onDisplay → retailAdRepository.postDisplayEvent
    // creative (ad_executable_id) 갱신 시 재호출
    if (executableId != null) {
      sendDisplayEvent(executableId);
    }
  }, [isVisible, executableId, logDisplayAds, sendDisplayEvent]);

  if (!isVisible) return null;

  if (isNil(retailAd?.ad_info)) {
    return null;
  }

  const bannerWidth = contentWidth - HORIZONTAL_PADDING * 2;
  const bannerHeight = retailAd.ad_info.creative_media_ratio
    ? bannerWidth / retailAd.ad_info.creative_media_ratio
    : bannerWidth * (72 / 222);

  return (
    <Flex
      padding={{ horizontal: HORIZONTAL_PADDING, vertical: 8 }}
      onPress={handlePress}
    >
      <Flex borderRadius={4} overflow="hidden">
        <RemoteImage
          uri={retailAd.ad_info.creative_media_link}
          width={bannerWidth}
          height={bannerHeight}
        />
      </Flex>
    </Flex>
  );
};
